import { useQuery } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';
import { catalogueKeys, fetchCategories, fetchOccasions } from '@/api/catalogue';
import { usePlatform } from '@/providers/PlatformProvider';
import { Skeleton, SkeletonRegion } from '@/components/ui/Skeleton';
import type { ProductFilters, TaxonomyTerm } from '@/types/catalogue';

/**
 * Catalogue facets — BUILD-PLAN §9, §27 (mobile filter drawer), §40.
 *
 * Rendered inline as a sidebar from `lg` and inside a drawer below it, so the
 * filter logic exists once. Facets come from the API, never a hard-coded
 * list, so a new occasion appears here the moment it is created (§17).
 *
 * ## Why this is not a column of checkboxes
 *
 * It was, and that is the default every framework hands you rather than a
 * design decision. Three things were wrong with it, and none of them were
 * bugs:
 *
 *   1. **No visible state.** With nine occasions and four categories the
 *      selected ones scrolled out of view, so the only way to know what was
 *      applied was to scroll the whole rail. The single "Clear All" button
 *      meant removing one filter of four required removing all four and
 *      starting again. Applied filters now sit at the top as chips, each
 *      removable on its own — the customer sees their state and can edit any
 *      part of it.
 *   2. **Checkboxes cost a whole row each** and read as a form. Thirteen
 *      stacked rows is a settings page. Facets are now inline pills, which
 *      scan in a fraction of the vertical space, put far more of the taxonomy
 *      on screen at once, and look like a shop rather than a preferences
 *      dialog. Selection is still a real checkbox underneath — the visual
 *      change costs nothing in semantics or keyboard support.
 *   3. **Two empty number fields for price.** Almost nobody types a range;
 *      they pick a bracket. Presets do the common case in one tap, and the
 *      custom fields remain underneath for the person who genuinely wants
 *      £37–£64.
 */

interface FilterPanelProps {
  filters: ProductFilters;
  onToggleTerm: (key: 'categories' | 'occasions', slug: string) => void;
  onSetFilter: (updates: Partial<ProductFilters>) => void;
  onClear: () => void;
  hasFilters: boolean;
}

/**
 * Price brackets, in major units.
 *
 * Open-ended at both ends: the first has no floor and the last no ceiling, so
 * every product in the catalogue falls into exactly one bracket and none of
 * them can return nothing.
 */
const PRICE_BRACKETS: Array<{ min?: number; max?: number }> = [
  { max: 25 },
  { min: 25, max: 50 },
  { min: 50, max: 100 },
  { min: 100 },
];

export function FilterPanel({
  filters,
  onToggleTerm,
  onSetFilter,
  onClear,
  hasFilters,
}: FilterPanelProps) {
  const { config } = usePlatform();

  const categories = useQuery({
    queryKey: catalogueKeys.categories,
    queryFn: fetchCategories,
    // Taxonomy changes rarely; refetching per navigation is wasted traffic.
    staleTime: 10 * 60 * 1000,
  });

  const occasions = useQuery({
    queryKey: catalogueKeys.occasions,
    queryFn: fetchOccasions,
    staleTime: 10 * 60 * 1000,
  });

  /**
   * Whole units only — a bracket label reading "£25.00" is noise.
   *
   * Two things this has to survive, both of which silently produced bare
   * numbers ("Under 25") in the first version:
   *
   *   - Backend locales are written `en_GB`. `Intl` requires BCP-47, rejects
   *     the underscore and THROWS, so the whole label fell into the catch and
   *     lost its currency symbol.
   *   - The catch itself dropped the currency entirely. A price filter that
   *     does not say what money it is in is worse than an unformatted one, so
   *     the fallback now retries with the browser's own locale and only then
   *     gives up to a plain code-prefixed string.
   */
  const money = (value: number) => {
    const currency = config.commerce.currency;
    const locale = config.commerce.locale?.replace(/_/g, '-');

    for (const candidate of [locale, undefined]) {
      try {
        return new Intl.NumberFormat(candidate, {
          style: 'currency',
          currency,
          maximumFractionDigits: 0,
        }).format(value);
      } catch {
        // Try the next candidate.
      }
    }

    return `${currency} ${value}`;
  };

  const bracketLabel = ({ min, max }: { min?: number; max?: number }) => {
    if (min === undefined) return `Under ${money(max!)}`;
    if (max === undefined) return `${money(min)} +`;
    return `${money(min)} – ${money(max)}`;
  };

  const isBracketActive = ({ min, max }: { min?: number; max?: number }) =>
    filters.min_price === min && filters.max_price === max;

  /*
   | Every applied filter, flattened into one removable list.
   |
   | Built from the SAME state the controls read, so a chip can never show a
   | filter that is not applied, or miss one that is.
   */
  const activeChips: Array<{ key: string; label: string; remove: () => void }> = [
    ...(filters.categories ?? []).map((slug) => ({
      key: `c-${slug}`,
      label: categories.data?.find((term) => term.slug === slug)?.name ?? slug,
      remove: () => onToggleTerm('categories', slug),
    })),
    ...(filters.occasions ?? []).map((slug) => ({
      key: `o-${slug}`,
      label: occasions.data?.find((term) => term.slug === slug)?.name ?? slug,
      remove: () => onToggleTerm('occasions', slug),
    })),
    ...(filters.min_price !== undefined || filters.max_price !== undefined
      ? [
          {
            key: 'price',
            label: bracketLabel({
              ...(filters.min_price !== undefined ? { min: filters.min_price } : {}),
              ...(filters.max_price !== undefined ? { max: filters.max_price } : {}),
            }),
            remove: () => onSetFilter({ min_price: undefined, max_price: undefined }),
          },
        ]
      : []),
    ...(filters.personalisable === true
      ? [
          {
            key: 'personalisable',
            label: 'Personalisable',
            remove: () => onSetFilter({ personalisable: undefined }),
          },
        ]
      : []),
    ...(filters.in_stock === true
      ? [
          {
            key: 'in_stock',
            label: 'In stock',
            remove: () => onSetFilter({ in_stock: undefined }),
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col">
      {hasFilters && activeChips.length > 0 && (
        <section aria-label="Applied filters" className="pb-6">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-[0.6875rem] uppercase tracking-[0.2em] text-muted">
              Applied
            </h3>

            <button
              type="button"
              onClick={onClear}
              className="text-xs tracking-wide text-muted underline-offset-4 transition-colors hover:text-accent hover:underline"
            >
              Clear all
            </button>
          </div>

          <ul className="mt-3 flex flex-wrap gap-2">
            {activeChips.map((chip) => (
              <li key={chip.key}>
                {/*
                  A chip is a REMOVE control, so it is a button that says so.
                  Labelling it "Remove X" rather than just "X" means a screen
                  reader announces the action, not only the filter name.
                */}
                <button
                  type="button"
                  onClick={chip.remove}
                  aria-label={`Remove filter: ${chip.label}`}
                  className="inline-flex min-h-8 items-center gap-1.5 rounded-full border border-accent/40 bg-accent/[0.07] px-3 text-xs text-ink transition-colors hover:border-accent hover:bg-accent/15"
                >
                  {chip.label}
                  <X className="h-3 w-3 text-muted" strokeWidth={2} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <FacetGroup
        legend="Category"
        terms={categories.data ?? []}
        isLoading={categories.isPending}
        selected={filters.categories ?? []}
        onToggle={(slug) => onToggleTerm('categories', slug)}
      />

      <FacetGroup
        legend="Occasion"
        terms={occasions.data ?? []}
        isLoading={occasions.isPending}
        selected={filters.occasions ?? []}
        onToggle={(slug) => onToggleTerm('occasions', slug)}
      />

      <Group legend="Price">
        <div className="flex flex-wrap gap-2">
          {PRICE_BRACKETS.map((bracket) => {
            const active = isBracketActive(bracket);

            return (
              <button
                key={bracketLabel(bracket)}
                type="button"
                aria-pressed={active}
                onClick={() =>
                  /* Tapping the active bracket clears it — a set of presets
                     with no way back is a trap. */
                  onSetFilter(
                    active
                      ? { min_price: undefined, max_price: undefined }
                      : { min_price: bracket.min, max_price: bracket.max },
                  )
                }
                className={pillClass(active)}
              >
                {bracketLabel(bracket)}
              </button>
            );
          })}
        </div>

        <div className="mt-3 flex items-center gap-2">
          <PriceInput
            label="Minimum price"
            value={filters.min_price}
            onCommit={(value) => onSetFilter({ min_price: value })}
          />
          <span className="text-muted" aria-hidden="true">
            –
          </span>
          <PriceInput
            label="Maximum price"
            value={filters.max_price}
            onCommit={(value) => onSetFilter({ max_price: value })}
          />
        </div>
      </Group>

      <Group legend="Options">
        <div className="flex flex-wrap gap-2">
          <TogglePill
            label="Can be personalised"
            checked={filters.personalisable === true}
            onChange={(checked) => onSetFilter({ personalisable: checked || undefined })}
          />
          <TogglePill
            label="In stock only"
            checked={filters.in_stock === true}
            onChange={(checked) => onSetFilter({ in_stock: checked || undefined })}
          />
        </div>
      </Group>
    </div>
  );
}

/**
 * One pill style, used by every selectable control here.
 *
 * Facets, price brackets and option toggles all mean "this narrows the
 * results", so they look alike on purpose — three different shapes for one
 * idea is what made the old panel read as a form.
 */
function pillClass(active: boolean): string {
  return [
    'inline-flex min-h-9 items-center gap-1.5 rounded-full border px-3.5 text-xs tracking-wide transition-colors',
    active
      ? 'border-accent bg-accent text-white'
      : 'border-line bg-surface text-ink hover:border-accent hover:text-accent',
  ].join(' ');
}

/** A titled block with a hairline above it, so groups read as groups. */
function Group({
  legend,
  children,
}: {
  legend: string;
  children: React.ReactNode;
}) {
  return (
    <fieldset className="border-t border-line py-6">
      <legend className="sr-only">{legend}</legend>

      <h3 className="mb-3.5 text-[0.6875rem] uppercase tracking-[0.2em] text-ink">
        {legend}
      </h3>

      {children}
    </fieldset>
  );
}

function FacetGroup({
  legend,
  terms,
  isLoading,
  selected,
  onToggle,
}: {
  legend: string;
  terms: TaxonomyTerm[];
  isLoading: boolean;
  selected: string[];
  onToggle: (slug: string) => void;
}) {
  if (isLoading) {
    return (
      <Group legend={legend}>
        <SkeletonRegion label={`Loading ${legend} filters`}>
          {/* Varied widths, because real facet names are not all the same
              length — equal blocks read as a loading bar, not as pills. */}
          <div className="flex flex-wrap gap-2">
            {['w-20', 'w-28', 'w-24', 'w-16', 'w-24'].map((width, index) => (
              <Skeleton key={index} className={`h-9 rounded-full ${width}`} />
            ))}
          </div>
        </SkeletonRegion>
      </Group>
    );
  }

  // An empty facet is hidden rather than shown as an empty box — §86, no
  // controls that cannot do anything.
  if (terms.length === 0) {
    return null;
  }

  return (
    <Group legend={legend}>
      <div className="flex flex-wrap gap-2">
        {terms.map((term) => {
          const active = selected.includes(term.slug);

          return (
            <FacetPill
              key={term.slug}
              label={term.name}
              count={term.productCount}
              checked={active}
              onChange={() => onToggle(term.slug)}
            />
          );
        })}
      </div>
    </Group>
  );
}

/**
 * A pill that is a real checkbox.
 *
 * The input is visually hidden rather than replaced by a div-with-onClick, so
 * the control keeps native checkbox semantics, keyboard operation and the
 * grouping a screen reader announces. Only the paint changed.
 */
function FacetPill({
  label,
  count,
  checked,
  onChange,
}: {
  label: string;
  count?: number | undefined;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className={`${pillClass(checked)} cursor-pointer has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="sr-only"
      />

      {checked && <Check className="h-3 w-3 shrink-0" strokeWidth={2.5} aria-hidden="true" />}

      <span>{label}</span>

      {count !== undefined && (
        <span className={checked ? 'text-white/70' : 'text-muted'}>{count}</span>
      )}
    </label>
  );
}

/** The same pill, for a plain on/off option with no count. */
function TogglePill({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className={`${pillClass(checked)} cursor-pointer has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="sr-only"
      />

      {checked && <Check className="h-3 w-3 shrink-0" strokeWidth={2.5} aria-hidden="true" />}

      <span>{label}</span>
    </label>
  );
}

function PriceInput({
  label,
  value,
  onCommit,
}: {
  label: string;
  value: number | undefined;
  onCommit: (value: number | undefined) => void;
}) {
  return (
    <input
      type="number"
      min={0}
      inputMode="numeric"
      aria-label={label}
      placeholder={label.includes('Minimum') ? 'Min' : 'Max'}
      /*
       | `key` forces a remount when the value changes from elsewhere.
       |
       | This is an uncontrolled input (defaultValue), so picking a preset
       | bracket would update the filter without the fields ever showing it —
       | the panel would then display a price filter the inputs disagreed
       | with. Keying on the value keeps the two in step.
       */
      key={value ?? 'empty'}
      defaultValue={value ?? ''}
      /*
       | Committed on blur rather than on every keystroke: filtering per
       | character would issue a request per digit and rewrite history
       | entries as the customer types.
       */
      onBlur={(event) => {
        const raw = event.target.value.trim();
        onCommit(raw === '' ? undefined : Number(raw));
      }}
      className="min-h-10 w-full min-w-0 rounded-sm border border-line bg-surface px-2.5 text-sm"
    />
  );
}
